import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Tv, 
  Film, 
  Search, 
  ShieldCheck, 
  User as UserIcon, 
  LogOut, 
  Menu, 
  X, 
  Sparkles,
  Home, 
  Layers, 
  Monitor, 
  RotateCw, 
  Star, 
  ChevronDown,
  PlusCircle,
  FolderOpen,
  Check,
  Radio
} from 'lucide-react';
import { MediaItem, UserProfile, DeviceMode, CuratedCollection } from '../types';

interface NavbarProps {
  currentView: string;
  onNavigate: (view: string, category?: string) => void;
  user: UserProfile | null;
  onOpenAuth: () => void;
  onLogout: () => void;
  onOpenAdmin: () => void;
  onOpenProfile: () => void;
  catalog: MediaItem[];
  collections?: CuratedCollection[];
  selectedCollectionId?: string | null;
  onSelectCollection?: (collectionId: string | null) => void;
  onSelectMedia: (item: MediaItem) => void;
  deviceMode?: DeviceMode;
  isTv?: boolean;
  onOpenDeviceSelector?: () => void;
  onOpenShortcuts?: () => void;
  onRefresh?: () => void;
  isSyncing?: boolean;
  syncStatusText?: string;
  isSearchOpenExternal?: boolean;
  onToggleSearchExternal?: (open: boolean) => void;
  isUserMenuOpenExternal?: boolean;
  onToggleUserMenuExternal?: (open: boolean) => void;
  onSearchSubmit?: (query: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  currentView,
  onNavigate,
  user,
  onOpenAuth,
  onLogout,
  onOpenAdmin,
  onOpenProfile,
  catalog,
  collections = [],
  selectedCollectionId = null,
  onSelectCollection,
  onSelectMedia,
  deviceMode = 'auto',
  isTv = false,
  onOpenDeviceSelector,
  onOpenShortcuts,
  onRefresh,
  isSyncing = false,
  syncStatusText = '',
  isSearchOpenExternal,
  onToggleSearchExternal,
  isUserMenuOpenExternal,
  onToggleUserMenuExternal,
  onSearchSubmit
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [internalSearchOpen, setInternalSearchOpen] = useState(false);
  const [internalUserMenuOpen, setInternalUserMenuOpen] = useState(false);
  const [isCollectionsDropdownOpen, setIsCollectionsDropdownOpen] = useState(false);
  
  const isSearchOpen = isSearchOpenExternal !== undefined ? isSearchOpenExternal : internalSearchOpen;
  const setIsSearchOpen = (open: boolean) => {
    if (onToggleSearchExternal) {
      onToggleSearchExternal(open);
    } else {
      setInternalSearchOpen(open);
    }
  };

  const isUserMenuOpen = isUserMenuOpenExternal !== undefined ? isUserMenuOpenExternal : internalUserMenuOpen;
  const setIsUserMenuOpen = (open: boolean) => {
    if (onToggleUserMenuExternal) {
      onToggleUserMenuExternal(open);
    } else {
      setInternalUserMenuOpen(open);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState<'all' | 'series' | 'movies'>('all');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const collectionsDropdownRef = useRef<HTMLDivElement>(null);
  const activeSyncing = isSyncing || isManualRefreshing;

  // Find active collection object
  const activeCollection = collections.find(c => c.id === selectedCollectionId);

  // Handle Search Submission
  const handleSearchSubmit = useCallback((overrideQuery?: string) => {
    const query = (overrideQuery !== undefined ? overrideQuery : searchQuery).trim();
    if (!query) return;

    setIsSearchOpen(false);
    if (onSearchSubmit) {
      onSearchSubmit(query);
    } else {
      onNavigate('home');
    }
  }, [searchQuery, onSearchSubmit, onNavigate]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isSearchOpen) {
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.classList.add('tv-focused');
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (collectionsDropdownRef.current && !collectionsDropdownRef.current.contains(event.target as Node)) {
        setIsCollectionsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefreshClick = useCallback(async () => {
    setIsManualRefreshing(true);
    setRefreshSuccess(false);
    
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        window.location.reload();
      }
      setRefreshSuccess(true);
    } catch (err) {
      console.warn('Sync error:', err);
    } finally {
      setIsManualRefreshing(false);
      setTimeout(() => setRefreshSuccess(false), 3000);
    }
  }, [onRefresh]);

  // Close search on Escape key or clear text first
  useEffect(() => {
    if (!isSearchOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (searchQuery.trim() !== '') {
          setSearchQuery('');
        } else {
          setIsSearchOpen(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, searchQuery]);

  // Filtered search results
  const filteredCatalog = catalog.filter(item => {
    if (searchFilter === 'series') return item.type === 'series';
    if (searchFilter === 'movies') return item.type === 'movie';
    return true;
  });

  const searchResults = searchQuery.trim() === '' ? [] : filteredCatalog.filter(item => {
    const q = searchQuery.toLowerCase().trim();
    return (
      item.title.toLowerCase().includes(q) ||
      (item.originalTitle && item.originalTitle.toLowerCase().includes(q)) ||
      item.genres.some(g => g.toLowerCase().includes(q)) ||
      (item.summary && item.summary.toLowerCase().includes(q)) ||
      (item.collectionName && item.collectionName.toLowerCase().includes(q))
    );
  }).slice(0, 18);

  const POPULAR_SEARCH_CHIPS = [
    'Bola de Drac', 
    'Detectiu Conan', 
    'One Piece', 
    'Inuyasha', 
    'Sakura', 
    'Shin-chan', 
    'Ranma', 
    'Slam Dunk', 
    'Death Note', 
    'Bleach',
    'Dr. Slump',
    'Doraemon'
  ];

  const isAdminUser = Boolean(
    user && (user.role === 'admin' || user.email === 'admin@animecat.cat' || user.email === 'josep.g.frigole2@gmail.com')
  );

  return (
    <header 
      id="main-navbar"
      className={`sticky top-0 left-0 right-0 z-40 transition-all duration-200 ${
        isTv 
          ? 'bg-[#070b14] border-b border-sky-500/20 py-2' 
          : isScrolled
            ? 'bg-[#070b14]/95 backdrop-blur-md shadow-md shadow-black/60 border-b border-white/10 py-2'
            : 'bg-[#070b14] border-b border-white/10 py-2 sm:py-2.5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          
          {/* Brand Logo */}
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6">
            <button 
              id="logo-button"
              data-focusable="true"
              onClick={() => {
                onNavigate('home');
                if (onSelectCollection) onSelectCollection(null);
                if (isSearchOpen) setIsSearchOpen(false);
              }} 
              className="flex items-center gap-2 sm:gap-2.5 group text-left focus:outline-none shrink-0"
              title="GarCollections Inici"
            >
              <div className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 flex items-center justify-center shadow-lg shadow-sky-500/25 group-hover:scale-105 transition-transform">
                <Layers className="w-5 h-5 text-white" />
                <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-cyan-400 border-2 border-black" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-display font-black text-lg sm:text-xl tracking-tight text-white group-hover:text-sky-400 transition-colors">
                  Gar<span className="text-sky-400">Collections</span>
                </span>
                <span className="px-1.5 py-0.5 rounded text-[8px] sm:text-[9px] font-bold uppercase tracking-wider bg-sky-500/20 text-sky-300 border border-sky-500/30">
                  HUB
                </span>
              </div>
            </button>

            {/* Core Navigation Bar */}
            <nav className="hidden sm:flex items-center gap-1.5 md:gap-2">
              
              {/* 1. Inici */}
              <button
                id="nav-home"
                data-focusable="true"
                onClick={() => {
                  onNavigate('home');
                  if (onSelectCollection) onSelectCollection(null);
                  if (isSearchOpen) setIsSearchOpen(false);
                }}
                className={`group relative flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none ${
                  currentView === 'home' && !isSearchOpen && !selectedCollectionId
                    ? 'text-sky-400 bg-sky-500/20 border border-sky-500/40 shadow-sm shadow-sky-500/10 font-semibold' 
                    : 'text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20'
                }`}
                title="Inici"
              >
                <Home className={`w-4 h-4 shrink-0 transition-transform duration-150 group-hover:scale-110 group-focus:scale-110 ${
                  currentView === 'home' && !selectedCollectionId ? 'text-sky-400 fill-sky-400/20' : 'text-slate-300'
                }`} />
                <span className="max-w-0 opacity-0 overflow-hidden whitespace-nowrap transition-all duration-150 ease-out group-hover:max-w-xs group-hover:opacity-100 group-focus:max-w-xs group-focus:opacity-100 group-focus-visible:max-w-xs group-focus-visible:opacity-100 group-[.tv-focused]:max-w-xs group-[.tv-focused]:opacity-100 text-xs sm:text-sm font-semibold">
                  Inici
                </span>
              </button>

              {/* 2. Col·leccions (Botó directe a la pantalla principal de col·leccions) */}
              <button
                id="nav-collections"
                data-focusable="true"
                onClick={() => {
                  if (onSelectCollection) onSelectCollection(null);
                  onNavigate('collections');
                }}
                className={`group relative flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none ${
                  currentView === 'collections'
                    ? 'text-sky-400 bg-sky-500/20 border border-sky-500/40 shadow-sm shadow-sky-500/10 font-semibold' 
                    : 'text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20'
                }`}
                title="Col·leccions"
              >
                <Layers className={`w-4 h-4 shrink-0 transition-transform duration-150 group-hover:scale-110 group-focus:scale-110 ${
                  currentView === 'collections' ? 'text-sky-400' : 'text-sky-400'
                }`} />
                <span className="text-xs sm:text-sm font-semibold">
                  Col·leccions
                </span>
              </button>

              {/* 3. Sèries */}
              <button
                id="nav-series"
                data-focusable="true"
                onClick={() => {
                  onNavigate('series');
                  if (isSearchOpen) setIsSearchOpen(false);
                }}
                className={`group relative flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none ${
                  currentView === 'series' && !isSearchOpen
                    ? 'text-sky-400 bg-sky-500/20 border border-sky-500/40 shadow-sm shadow-sky-500/10 font-semibold' 
                    : 'text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20'
                }`}
                title="Sèries"
              >
                <Tv className={`w-4 h-4 shrink-0 transition-transform duration-150 group-hover:scale-110 group-focus:scale-110 ${
                  currentView === 'series' && !isSearchOpen ? 'text-sky-400' : 'text-blue-400'
                }`} />
                <span className="max-w-0 opacity-0 overflow-hidden whitespace-nowrap transition-all duration-150 ease-out group-hover:max-w-xs group-hover:opacity-100 group-focus:max-w-xs group-focus:opacity-100 group-focus-visible:max-w-xs group-focus-visible:opacity-100 group-[.tv-focused]:max-w-xs group-[.tv-focused]:opacity-100 text-xs sm:text-sm font-semibold">
                  Sèries
                </span>
              </button>

              {/* 4. Pel·lícules */}
              <button
                id="nav-movies"
                data-focusable="true"
                onClick={() => {
                  onNavigate('movies');
                  if (isSearchOpen) setIsSearchOpen(false);
                }}
                className={`group relative flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none ${
                  currentView === 'movies' && !isSearchOpen
                    ? 'text-sky-400 bg-sky-500/20 border border-sky-500/40 shadow-sm shadow-sky-500/10 font-semibold' 
                    : 'text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20'
                }`}
                title="Pel·lícules"
              >
                <Film className={`w-4 h-4 shrink-0 transition-transform duration-150 group-hover:scale-110 group-focus:scale-110 ${
                  currentView === 'movies' && !isSearchOpen ? 'text-sky-400' : 'text-cyan-400'
                }`} />
                <span className="max-w-0 opacity-0 overflow-hidden whitespace-nowrap transition-all duration-150 ease-out group-hover:max-w-xs group-hover:opacity-100 group-focus:max-w-xs group-focus:opacity-100 group-focus-visible:max-w-xs group-focus-visible:opacity-100 group-[.tv-focused]:max-w-xs group-[.tv-focused]:opacity-100 text-xs sm:text-sm font-semibold">
                  Pel·lícules
                </span>
              </button>

              {/* 4.5. IPTV */}
              <button
                id="nav-iptv"
                data-focusable="true"
                onClick={() => {
                  onNavigate('iptv');
                  if (isSearchOpen) setIsSearchOpen(false);
                }}
                className={`group relative flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none ${
                  currentView === 'iptv' && !isSearchOpen
                    ? 'text-red-400 bg-red-500/20 border border-red-500/40 shadow-sm shadow-red-500/10 font-semibold' 
                    : 'text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20'
                }`}
                title="IPTV & Canals en directe"
              >
                <Radio className={`w-4 h-4 shrink-0 transition-transform duration-150 group-hover:scale-110 group-focus:scale-110 ${
                  currentView === 'iptv' && !isSearchOpen ? 'text-red-400' : 'text-red-400'
                }`} />
                <span className="max-w-0 opacity-0 overflow-hidden whitespace-nowrap transition-all duration-150 ease-out group-hover:max-w-xs group-hover:opacity-100 group-focus:max-w-xs group-focus:opacity-100 group-focus-visible:max-w-xs group-focus-visible:opacity-100 group-[.tv-focused]:max-w-xs group-[.tv-focused]:opacity-100 text-xs sm:text-sm font-semibold">
                  IPTV
                </span>
              </button>

              {/* 5. Cerca */}
              <button
                id="nav-search"
                data-focusable="true"
                onClick={() => {
                  setIsSearchOpen(!isSearchOpen);
                }}
                className={`group relative flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-sm font-medium transition-colors duration-150 focus:outline-none ${
                  isSearchOpen
                    ? 'text-sky-400 bg-sky-500/20 border border-sky-500/40 shadow-sm shadow-sky-500/10 font-semibold' 
                    : 'text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20'
                }`}
                title="Cerca al catàleg"
              >
                <Search className={`w-4 h-4 shrink-0 transition-transform duration-150 group-hover:scale-110 group-focus:scale-110 ${
                  isSearchOpen ? 'text-sky-400' : 'text-sky-400'
                }`} />
                <span className="max-w-0 opacity-0 overflow-hidden whitespace-nowrap transition-all duration-150 ease-out group-hover:max-w-xs group-hover:opacity-100 group-focus:max-w-xs group-focus:opacity-100 group-focus-visible:max-w-xs group-focus-visible:opacity-100 group-[.tv-focused]:max-w-xs group-[.tv-focused]:opacity-100 text-xs sm:text-sm font-semibold">
                  Cerca
                </span>
              </button>
            </nav>
          </div>

          {/* Right Section: User & Management Controls */}
          <div className="flex items-center gap-2">
            
            {/* User Profile / Options Trigger */}
            <div className="relative" ref={userMenuRef}>
              {user ? (
                <button
                  id="user-profile-button"
                  data-focusable="true"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center gap-2 p-1 pl-2.5 pr-1.5 rounded-full border transition-all focus:outline-none ${
                    isUserMenuOpen 
                      ? 'bg-sky-500/20 border-sky-400 ring-2 ring-sky-400/40' 
                      : 'bg-white/5 hover:bg-white/10 border-white/15 text-slate-200'
                  }`}
                  title="Menú d'opcions d'usuari i gestió"
                >
                  <span className="text-xs font-semibold text-slate-200 hidden md:inline max-w-[120px] truncate">
                    {user.displayName || user.username}
                  </span>
                  <img
                    src={user.avatar}
                    alt={user.displayName}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border-2 border-sky-400 shadow-md"
                    referrerPolicy="no-referrer"
                  />
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isUserMenuOpen ? 'rotate-180 text-sky-400' : ''}`} />
                </button>
              ) : (
                <button
                  id="user-profile-button"
                  data-focusable="true"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all focus:outline-none ${
                    isUserMenuOpen 
                      ? 'bg-sky-500/20 border-sky-400 ring-2 ring-sky-400/40' 
                      : 'bg-white/5 hover:bg-white/10 border-white/15 text-slate-200'
                  }`}
                  title="Opcions i gestió"
                >
                  <UserIcon className="w-3.5 h-3.5 text-slate-300" />
                  <span className="text-xs font-semibold">Opcions</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isUserMenuOpen ? 'rotate-180 text-sky-400' : ''}`} />
                </button>
              )}

              {/* User Dropdown Menu */}
              {isUserMenuOpen && (
                <div 
                  id="user-dropdown-menu"
                  className="absolute right-0 mt-2 w-72 sm:w-80 bg-[#0d1322] rounded-2xl p-2.5 shadow-2xl z-50 border border-white/15 backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  {/* User Profile Header or Guest Header */}
                  {user ? (
                    <div className="flex items-center gap-3 p-2.5 border-b border-white/10 bg-white/5 rounded-xl mb-1.5">
                      <img
                        src={user.avatar}
                        alt={user.displayName}
                        className="w-10 h-10 rounded-full object-cover border-2 border-sky-400 shrink-0"
                        referrerPolicy="no-referrer"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{user.displayName}</p>
                        <p className="text-xs text-slate-400 font-mono truncate">@{user.username}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider ${
                            user.role === 'admin' 
                              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                              : 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                          }`}>
                            {user.role === 'admin' ? 'Administrador del Sistema' : 'El Teu Catàleg'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-2.5 border-b border-white/10 bg-white/5 rounded-xl mb-1.5 flex items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-bold text-white">Opcions d'Usuari</p>
                        <p className="text-[11px] text-slate-400">Mode convidat / local</p>
                      </div>
                      <button
                        type="button"
                        data-focusable="true"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          onOpenAuth();
                        }}
                        className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs shadow-md transition-all"
                      >
                        Entrar
                      </button>
                    </div>
                  )}

                  {/* Options List */}
                  <div className="space-y-1 text-xs">
                    
                    {/* 1. Panell de Gestió de Contingut (Accessible to ALL users) */}
                    <button
                      id="dropdown-management-button"
                      data-focusable="true"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        onOpenAdmin();
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-sky-300 hover:text-white bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 rounded-xl transition-all text-left group"
                    >
                      <div className="flex items-center gap-2.5">
                        <PlusCircle className="w-4 h-4 text-sky-400 group-hover:scale-110 transition-transform" />
                        <span className="font-bold">Panell de Gestió (Afegir, editar, pujar JSON)</span>
                      </div>
                      <span className="text-[9px] font-bold bg-sky-500 text-black px-1.5 py-0.5 rounded">
                        GESTIÓ
                      </span>
                    </button>

                    {/* 2. Sincronització de fonts i actualització de catàleg */}
                    <button
                      id="dropdown-sync-button"
                      data-focusable="true"
                      onClick={() => {
                        handleRefreshClick();
                      }}
                      disabled={activeSyncing}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-slate-200 hover:text-white hover:bg-white/10 rounded-xl transition-all text-left group"
                    >
                      <div className="flex items-center gap-2.5">
                        <RotateCw className={`w-4 h-4 text-sky-400 ${activeSyncing ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                        <span className="font-semibold">Sincronitza Catàleg</span>
                      </div>
                      <span className="text-[10px] text-slate-400 bg-white/10 px-2 py-0.5 rounded font-medium">
                        {refreshSuccess ? 'Completat!' : activeSyncing ? 'Sincronitzant...' : `${catalog.length} títols`}
                      </span>
                    </button>

                    {/* 3. Selector de Mode de pantalla */}
                    {onOpenDeviceSelector && (
                      <button
                        id="dropdown-device-selector-button"
                        data-focusable="true"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          onOpenDeviceSelector();
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-slate-200 hover:text-white hover:bg-white/10 rounded-xl transition-all text-left group"
                      >
                        <div className="flex items-center gap-2.5">
                          {isTv ? <Tv className="w-4 h-4 text-sky-400" /> : <Monitor className="w-4 h-4 text-sky-400" />}
                          <span className="font-semibold">Mode de Pantalla</span>
                        </div>
                        <span className="text-[10px] text-sky-300 bg-sky-500/10 border border-sky-500/20 px-2 py-0.5 rounded font-bold uppercase">
                          {isTv ? 'Mode TV' : deviceMode === 'auto' ? 'Auto' : deviceMode}
                        </span>
                      </button>
                    )}

                    <div className="border-t border-white/10 my-1 pt-1" />

                    {/* 4. Configurar Perfil & Avatar */}
                    <button
                      id="dropdown-profile-button"
                      data-focusable="true"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        onOpenProfile();
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-slate-200 hover:text-white hover:bg-white/10 rounded-xl transition-all text-left"
                    >
                      <UserIcon className="w-4 h-4 text-slate-400" />
                      <span>Configurar Perfil & Avatar</span>
                    </button>

                    {/* 5. Tancar Sessió */}
                    <button
                      id="dropdown-logout-button"
                      data-focusable="true"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        onLogout();
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/15 rounded-xl transition-all text-left font-semibold"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Tancar Sessió</span>
                    </button>

                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Hamburger */}
            <button
              id="mobile-menu-button"
              data-focusable="true"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="sm:hidden p-2 rounded-xl text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10"
              title="Menú"
            >
              {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4 text-white" />}
            </button>

          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="sm:hidden mt-2 pt-2 border-t border-white/10 space-y-1 bg-[#0d1322] rounded-2xl p-3 shadow-xl">
            <button
              data-focusable="true"
              onClick={() => {
                onNavigate('home');
                if (onSelectCollection) onSelectCollection(null);
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-left ${
                currentView === 'home' && !selectedCollectionId ? 'bg-sky-500/20 text-sky-300 font-semibold' : 'text-slate-200 hover:bg-white/10'
              }`}
            >
              <Sparkles className="w-4 h-4 text-sky-400" />
              <span>Inici</span>
            </button>

            <button
              data-focusable="true"
              onClick={() => {
                onNavigate('collections');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-left ${
                currentView === 'collections' ? 'bg-sky-500/20 text-sky-300 font-semibold' : 'text-slate-200 hover:bg-white/10'
              }`}
            >
              <Layers className="w-4 h-4 text-sky-400" />
              <span>Col·leccions ({collections.length})</span>
            </button>

            <button
              data-focusable="true"
              onClick={() => {
                onNavigate('series');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-left ${
                currentView === 'series' ? 'bg-sky-500/20 text-sky-300 font-semibold' : 'text-slate-200 hover:bg-white/10'
              }`}
            >
              <Tv className="w-4 h-4 text-blue-400" />
              <span>Sèries</span>
            </button>

            <button
              data-focusable="true"
              onClick={() => {
                onNavigate('movies');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-left ${
                currentView === 'movies' ? 'bg-sky-500/20 text-sky-300 font-semibold' : 'text-slate-200 hover:bg-white/10'
              }`}
            >
              <Film className="w-4 h-4 text-cyan-400" />
              <span>Pel·lícules</span>
            </button>

            <button
              data-focusable="true"
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsSearchOpen(true);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-left text-sky-400 hover:bg-white/10"
            >
              <Search className="w-4 h-4 text-sky-400" />
              <span>Cerca al Catàleg</span>
            </button>
          </div>
        )}

      </div>

      {/* Enriched Search Drawer */}
      {isSearchOpen && (
        <div 
          id="navbar-search-drawer"
          className="w-full border-t border-sky-500/40 bg-[#070c18]/98 backdrop-blur-2xl shadow-2xl shadow-black transition-all"
        >
          <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-5 space-y-3 sm:space-y-4">
            
            {/* Search Input Bar */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative flex-1 flex items-center bg-[#0e1628] border-2 border-sky-500/70 focus-within:border-sky-400 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 shadow-inner">
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-sky-400 shrink-0 mr-2 sm:mr-3" />
                <input
                  ref={searchInputRef}
                  id="search-input-field"
                  data-focusable="true"
                  type="text"
                  placeholder="Cerca per títol, col·lecció, gènere..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.keyCode === 13) {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                  className="w-full bg-transparent text-sm sm:text-base text-white placeholder-slate-400 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    data-focusable="true"
                    onClick={() => {
                      setSearchQuery('');
                      searchInputRef.current?.focus();
                    }}
                    className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-white/10 ml-1"
                    title="Netejar cerca"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Close Drawer Button */}
              <button
                id="close-search-drawer"
                data-focusable="true"
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearchQuery('');
                }}
                className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl bg-white/10 hover:bg-red-500/20 text-slate-300 hover:text-red-300 border border-white/10 hover:border-red-500/30 text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 shrink-0"
                title="Tancar cercador"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Tancar</span>
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-2">
              <div className="flex items-center gap-1.5 text-xs">
                <button
                  id="search-filter-all"
                  data-focusable="true"
                  onClick={() => setSearchFilter('all')}
                  className={`px-3 py-1 rounded-lg font-semibold transition-all ${
                    searchFilter === 'all'
                      ? 'bg-sky-500 text-black shadow-md'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  Tots els títols
                </button>
                <button
                  id="search-filter-series"
                  data-focusable="true"
                  onClick={() => setSearchFilter('series')}
                  className={`flex items-center gap-1 px-3 py-1 rounded-lg font-semibold transition-all ${
                    searchFilter === 'series'
                      ? 'bg-sky-500 text-black shadow-md'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  <Tv className="w-3 h-3" />
                  <span>Sèries</span>
                </button>
                <button
                  id="search-filter-movies"
                  data-focusable="true"
                  onClick={() => setSearchFilter('movies')}
                  className={`flex items-center gap-1 px-3 py-1 rounded-lg font-semibold transition-all ${
                    searchFilter === 'movies'
                      ? 'bg-sky-500 text-black shadow-md'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  <Film className="w-3 h-3" />
                  <span>Pel·lícules</span>
                </button>
              </div>

              <div className="text-[11px] text-slate-400 font-medium">
                {searchQuery.trim() !== '' 
                  ? `${searchResults.length} resultat${searchResults.length !== 1 ? 's' : ''}`
                  : `${filteredCatalog.length} títols disponibles`}
              </div>
            </div>

            {/* Popular Franchise Shortcuts */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none text-xs">
              <span className="text-slate-400 text-[11px] font-medium shrink-0 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-sky-400" />
                <span>Popular:</span>
              </span>
              {POPULAR_SEARCH_CHIPS.map((chip, idx) => (
                <button
                  key={chip}
                  id={`search-chip-${idx}`}
                  data-focusable="true"
                  onClick={() => {
                    setSearchQuery(chip);
                    handleSearchSubmit(chip);
                  }}
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium shrink-0 transition-all border ${
                    searchQuery.toLowerCase() === chip.toLowerCase()
                      ? 'bg-sky-500 text-black border-sky-400 font-bold'
                      : 'bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border-white/10 hover:border-sky-500/40'
                  }`}
                >
                  {chip}
                </button>
              ))}
            </div>

            {/* Search Results Grid */}
            <div className="pt-1">
              {searchResults.length > 0 ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-[48vh] overflow-y-auto pr-1">
                    {searchResults.map((item) => (
                      <button
                        key={item.id}
                        id={`search-result-${item.id}`}
                        data-focusable="true"
                        onClick={() => {
                          onSelectMedia(item);
                          setIsSearchOpen(false);
                        }}
                        className="group relative flex flex-col bg-[#0d1322] hover:bg-[#151f38] border border-white/10 hover:border-sky-400 rounded-xl overflow-hidden text-left transition-all p-1 sm:p-1.5 focus:ring-2 focus:ring-sky-400 focus:outline-none shadow-sm"
                      >
                        <div className="relative aspect-[2/3] w-full rounded-lg overflow-hidden mb-1 bg-slate-800">
                          <img
                            src={item.poster}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            referrerPolicy="no-referrer"
                          />
                          <div className="absolute top-1 left-1">
                            <span className="px-1 py-0.2 rounded bg-black/85 text-sky-300 text-[8px] font-bold uppercase tracking-wider border border-white/10">
                              {item.type === 'series' ? 'Sèrie' : 'Pel·li'}
                            </span>
                          </div>
                          {item.collectionName && (
                            <div className="absolute bottom-1 left-1 right-1">
                              <span className="px-1 py-0.2 rounded bg-black/80 text-sky-300 text-[7px] font-medium truncate block">
                                {item.collectionName}
                              </span>
                            </div>
                          )}
                        </div>
                        <h4 className="text-[11px] font-medium text-white group-hover:text-sky-400 transition-colors line-clamp-1">
                          {item.title}
                        </h4>
                        <div className="flex items-center justify-between text-[9px] text-slate-400 mt-0.5">
                          <span>
                            {item.type === 'series' ? `${item.numberOfEpisodes || item.episodes?.length || 0} eps` : `${item.runtime || 90}m`}
                          </span>
                          {item.rating && (
                            <span className="flex items-center gap-0.5 text-sky-400 font-semibold">
                              <Star className="w-2 h-2 fill-sky-400" />
                              <span>{item.rating.toFixed(1)}</span>
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : searchQuery.trim() !== '' ? (
                <div className="text-center py-6 text-slate-400 bg-white/5 rounded-2xl border border-white/10">
                  <p className="text-xs sm:text-sm text-slate-300 font-semibold">No s'han trobat títols per a "{searchQuery}"</p>
                  <p className="text-[11px] text-slate-400 mt-1">Proveu amb paraules clau o seleccioneu un dels suggeriments.</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[11px] text-slate-400 px-1 font-semibold uppercase tracking-wider">
                    <span>Recomanats del Catàleg</span>
                    <span className="text-slate-500 text-[9px]">Tria ràpida</span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-[46vh] overflow-y-auto pr-1">
                    {filteredCatalog.slice(0, 8).map((item) => (
                      <button
                        key={item.id}
                        id={`search-result-${item.id}`}
                        data-focusable="true"
                        onClick={() => {
                          onSelectMedia(item);
                          setIsSearchOpen(false);
                        }}
                        className="group relative flex flex-col bg-[#0d1322] hover:bg-[#151f38] border border-white/10 hover:border-sky-400 rounded-xl overflow-hidden text-left transition-all p-1 focus:ring-2 focus:ring-sky-400 focus:outline-none shadow-sm"
                      >
                        <div className="relative aspect-[2/3] w-full rounded-lg overflow-hidden mb-1 bg-slate-800">
                          <img
                            src={item.poster}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            referrerPolicy="no-referrer"
                          />
                          <div className="absolute top-1 left-1">
                            <span className="px-1 py-0.2 rounded bg-black/85 text-sky-300 text-[8px] font-bold">
                              {item.type === 'series' ? 'Sèrie' : 'Film'}
                            </span>
                          </div>
                        </div>
                        <h4 className="text-[11px] font-medium text-white group-hover:text-sky-400 transition-colors line-clamp-1">
                          {item.title}
                        </h4>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

    </header>
  );
};
